import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyMagicLink from './pages/VerifyMagicLink';
import ChangePassword from './pages/ChangePassword';
import SsoCallback from './pages/SsoCallback';
import Events from './pages/Events';
import MyBookings from './pages/MyBookings';
import Profile from './pages/Profile';
import AdminDashboard from './pages/AdminDashboard';
import AdminEvents from './pages/AdminEvents';
import AdminBookings from './pages/AdminBookings';
import AdminCalendar from './pages/AdminCalendar';
import AdminCalendarioReservas from './pages/AdminCalendarioReservas';
import AdminUsers from './pages/AdminUsers';
import QuestionPacks from './pages/QuestionPacks';
import AdminWhatsApp from './pages/AdminWhatsApp';
import AdminHospederias from './pages/AdminHospederias';
import AdminIncidencias from './pages/AdminIncidencias';
import AdminPortal from './pages/AdminPortal';
import AdminPersonas from './pages/AdminPersonas';
import FamilyPortal from './pages/FamilyPortal';
import MisPersonas from './pages/MisPersonas';
import ModeratorView from './pages/ModeratorView';
import AdminCampamento from './pages/AdminCampamento';
import AdminRoster from './pages/AdminRoster';
import AdminEstancias from './pages/AdminEstancias';
import AdminAlojamientos from './pages/AdminAlojamientos';
import Completar from './pages/Completar';
import EditarImagen from './pages/EditarImagen';
import PagarReserva from './pages/PagarReserva';
import ErrorBoundary from './components/ErrorBoundary';
import './styles/global.css';

function AppRoutes() {
  const { user, loading } = useAuth();

  // Página pública de completar datos (reservas de campamentoaldealix.com / MotoPress).
  // Accesible sin login y sin esperar a la verificación de sesión: el acceso lo
  // concede el token firmado del enlace del email.
  if (window.location.pathname.startsWith('/completar')) {
    return (
      <Routes>
        <Route path="/completar" element={<Completar />} />
      </Routes>
    );
  }

  // Página pública de autoservicio: editar la autorización de imagen (token del email).
  if (window.location.pathname.startsWith('/autorizacion-imagen')) {
    return (
      <Routes>
        <Route path="/autorizacion-imagen" element={<EditarImagen />} />
      </Routes>
    );
  }

  // Página pública de pago de reserva nativa (token del enlace del email).
  if (window.location.pathname.startsWith('/pagar-reserva')) {
    return (
      <Routes>
        <Route path="/pagar-reserva" element={<PagarReserva />} />
      </Routes>
    );
  }

  if (loading) return (
    <div className="loading-screen">
      <div className="rune">ᚠᚢᚦᚨᚱ</div>
      <div className="spinner" />
    </div>
  );

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/registro" element={<Register />} />
        <Route path="/auth/verify" element={<VerifyMagicLink />} />
        <Route path="/sso" element={<SsoCallback />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (user.mustChangePassword) {
    return (
      <Routes>
        <Route path="/cambiar-contrasena" element={<ChangePassword />} />
        <Route path="*" element={<Navigate to="/cambiar-contrasena" replace />} />
      </Routes>
    );
  }

  if (user.role === 'moderator') {
    return (
      <div className="app-shell">
        <Sidebar />
        <Routes>
          <Route path="/moderador" element={<ModeratorView />} />
          <Route path="/perfil" element={<Profile />} />
          <Route path="*" element={<Navigate to="/moderador" replace />} />
        </Routes>
      </div>
    );
  }

  // Rol limitado: acceso únicamente a la pestaña de Incidencias.
  if (user.role === 'incidencias') {
    return (
      <div className="app-shell">
        <Sidebar />
        <Routes>
          <Route path="/admin/incidencias" element={<AdminIncidencias />} />
          <Route path="/perfil" element={<Profile />} />
          <Route path="*" element={<Navigate to="/admin/incidencias" replace />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <Sidebar />
      <Routes>
        {user.role === 'admin' ? (
          <>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/eventos" element={<AdminEvents />} />
            <Route path="/admin/reservas" element={<AdminBookings />} />
            <Route path="/admin/campamento" element={<AdminCampamento />} />
            <Route path="/admin/roster" element={<AdminRoster />} />
            <Route path="/admin/estancias" element={<AdminEstancias />} />
            <Route path="/admin/alojamientos" element={<AdminAlojamientos />} />
            <Route path="/admin/calendario" element={<AdminCalendar />} />
            <Route path="/admin/calendario-reservas" element={<AdminCalendarioReservas />} />
            <Route path="/admin/usuarios" element={<AdminUsers />} />
            <Route path="/admin/whatsapp" element={<AdminWhatsApp />} />
            <Route path="/admin/preguntas" element={<QuestionPacks />} />
            <Route path="/admin/hospederias" element={<AdminHospederias />} />
            <Route path="/admin/incidencias" element={<AdminIncidencias />} />
            <Route path="/admin/portal" element={<AdminPortal />} />
            <Route path="/admin/personas" element={<AdminPersonas />} />
            <Route path="/perfil" element={<Profile />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </>
        ) : (
          <>
            <Route path="/eventos" element={<Events />} />
            <Route path="/mis-reservas" element={<MyBookings />} />
            <Route path="/mis-personas" element={<MisPersonas />} />
            <Route path="/portal" element={<FamilyPortal />} />
            <Route path="/perfil" element={<Profile />} />
            <Route path="*" element={<Navigate to="/eventos" replace />} />
          </>
        )}
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
